import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { Download, FolderOpen, Megaphone, Sparkles, X, Wallet, AlertTriangle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useToast } from "../components/Toast";
import { StatusPill } from "../components/StatusPill";

export default function Admin() {
  const toast = useToast();
  const { user } = useAuth();
  const isReviewer = user?.role === "reviewer";
  const [tab, setTab] = useState("receipts");
  const [receipts, setReceipts] = useState([]);
  const [cashouts, setCashouts] = useState([]);
  const [users, setUsers] = useState([]);

  // Receipt preview/action state
  const [previewId, setPreviewId] = useState(null);
  const [previewImg, setPreviewImg] = useState(null);
  const [previewReadOnly, setPreviewReadOnly] = useState(false);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [actionInput, setActionInput] = useState("");
  const [pointsInput, setPointsInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [receiptFilter, setReceiptFilter] = useState("all"); // all|pending|approved|rejected

  // Adjust user balance modal
  const [adjustUser, setAdjustUser] = useState(null);
  const [proofCashout, setProofCashout] = useState(null);
  const [proofReceipt, setProofReceipt] = useState(null);
  const [adjustAmt, setAdjustAmt] = useState("");
  const [adjustMsg, setAdjustMsg] = useState("");

  // Broadcast state
  const [broadcastTitle, setBroadcastTitle] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcasting, setBroadcasting] = useState(false);
  // Load lifecycle
  const [loadState, setLoadState] = useState("loading"); // loading | ok | error
  const [loadError, setLoadError] = useState(null);

  // Promo banner state
  const [promoText, setPromoText] = useState("");
  const [promoActive, setPromoActive] = useState(false);
  const [promoSaving, setPromoSaving] = useState(false);

  const load = useCallback(async () => {
    setLoadState((s) => (s === "ok" ? s : "loading"));
    setLoadError(null);
    try {
      if (isReviewer) {
        const r = await api.adminReceipts();
        setReceipts(r);
        setLoadState("ok");
        return;
      }
      const [r, c, u, p] = await Promise.all([
        api.adminReceipts(),
        api.adminCashouts(),
        api.adminUsers(),
        api.adminGetPromo(),
      ]);
      setReceipts(r);
      setCashouts(c);
      setUsers(u);
      setPromoText(p?.text || "");
      setPromoActive(!!p?.active);
      setLoadState("ok");
    } catch (e) {
      const status = e?.status;
      const msg = e?.message || "Failed to load";
      setLoadError({ status, message: msg });
      setLoadState("error");
      // Don't spam the toast on first-load failure — the inline error UI handles it.
      if (loadState === "ok") {
        toast.error(msg);
      }
    }
  }, [toast, isReviewer, loadState]);

  useEffect(() => {
    load();
  }, [load]);

  const openReceiptPreview = async (id, readOnly = false) => {
    try {
      const r = await api.receipt(id);
      setPreviewId(id);
      setPreviewImg(r.image_base64);
      setPreviewReadOnly(readOnly);
      setPreviewMeta(r);
      setActionInput("");
      setPointsInput("");
    } catch (e) {
      toast.error(e?.message || "Failed to load image");
    }
  };

  const closePreview = () => {
    setPreviewId(null);
    setPreviewImg(null);
    setPreviewReadOnly(false);
    setPreviewMeta(null);
  };

  const approveReceipt = async () => {
    if (!previewId) return;
    // Cash amount is mandatory (blank = £0 approval).
    if (actionInput.trim() === "") {
      toast.error("Enter the cash amount to award (£).");
      return;
    }
    const amt = parseFloat(actionInput);
    if (isNaN(amt) || amt < 0) {
      toast.error("Amount must be a positive number.");
      return;
    }
    setBusy(true);
    try {
      // Backend still accepts a legacy `points` param — pass 0.
      await api.adminApprove(previewId, amt, "Receipt approved", 0);
      closePreview();
      await load();
      toast.success(`Approved · £${amt.toFixed(2)} added to wallet`);
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const rejectReceipt = async () => {
    if (!previewId) return;
    setBusy(true);
    try {
      await api.adminReject(previewId, actionInput || "Receipt did not meet requirements");
      closePreview();
      await load();
      toast.success("Receipt rejected");
    } catch (e) {
      toast.error(e?.message || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const payCashout = async (id) => {
    if (!window.confirm("Mark cashout paid?\n\nOnly click this after you've actually sent the money from your bank. The user will be notified their payout has landed.")) return;
    try {
      await api.adminPay(id, "Funds sent — should land within 1-10 hours.");
      await load();
      toast.success("Marked as paid");
      // Auto-open the proof card so it's ready to screenshot
      const paid = (await api.adminCashouts("paid")).find((c) => c.id === id);
      if (paid) setProofCashout(paid);
    } catch (e) {
      toast.error(e?.message || "Failed");
    }
  };

  const rejectCashout = async (id) => {
    if (!window.confirm("Reject cashout\n\nThe held funds will be refunded to the user.")) return;
    try {
      await api.adminRejectCashout(id, "Cashout request rejected");
      await load();
      toast.success("Cashout rejected and refunded");
    } catch (e) {
      toast.error(e?.message || "Failed");
    }
  };

  const submitAdjust = async () => {
    if (!adjustUser) return;
    const amt = parseFloat(adjustAmt);
    if (isNaN(amt) || amt === 0) {
      toast.error("Enter a non-zero amount (use negative to deduct)");
      return;
    }
    try {
      await api.adminAdjustBalance(adjustUser.id, amt, adjustMsg || undefined);
      setAdjustUser(null);
      setAdjustAmt("");
      setAdjustMsg("");
      await load();
      toast.success("Balance adjusted");
    } catch (e) {
      toast.error(e?.message || "Failed");
    }
  };

  const recipients = users.filter((u) => u.role !== "admin").length;

  const downloadReceipt = (dataUrl, id) => {
    if (!dataUrl) return;
    // Detect mime type for extension
    const m = /^data:(image\/[a-zA-Z0-9.+-]+);base64,/.exec(dataUrl);
    const ext = m ? (m[1].split("/")[1].split("+")[0] || "jpg") : "jpg";
    const a = document.createElement("a");
    a.href = dataUrl;
    a.download = `receipt-${id}.${ext}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const sendBroadcast = async () => {
    const title = broadcastTitle.trim();
    const body = broadcastBody.trim();
    if (!title || !body) {
      toast.error("Please enter both a title and a message.");
      return;
    }
    if (!window.confirm(`Send broadcast?\n\nThis will send "${title}" to ${recipients} user${recipients === 1 ? "" : "s"}.`)) return;
    setBroadcasting(true);
    try {
      const res = await api.adminBroadcast(title, body);
      setBroadcastTitle("");
      setBroadcastBody("");
      toast.success(`Delivered to ${res.delivered} user${res.delivered === 1 ? "" : "s"}.`);
    } catch (e) {
      toast.error(e?.message || "Failed to send broadcast");
    } finally {
      setBroadcasting(false);
    }
  };

  const savePromo = async () => {
    const txt = promoText.trim();
    if (!txt) {
      toast.error("Please enter some promo text first.");
      return;
    }
    setPromoSaving(true);
    try {
      const res = await api.adminSetPromo(true, txt);
      setPromoActive(!!res.active);
      setPromoText(res.text || "");
      toast.success("Promo banner is now live on Home.");
    } catch (e) {
      toast.error(e?.message || "Failed to save promo");
    } finally {
      setPromoSaving(false);
    }
  };

  const clearPromo = async () => {
    if (!window.confirm("Remove promo banner?\n\nUsers will no longer see it on their Home page.")) return;
    setPromoSaving(true);
    try {
      await api.adminSetPromo(false, "");
      setPromoActive(false);
      setPromoText("");
      toast.success("Promo banner removed.");
    } catch (e) {
      toast.error(e?.message || "Failed to remove promo");
    } finally {
      setPromoSaving(false);
    }
  };

  const pendingReceipts = receipts.filter((r) => r.status === "pending");
  const approvedReceipts = receipts.filter((r) => r.status === "approved");
  const rejectedReceipts = receipts.filter((r) => r.status === "rejected");
  const visibleReceipts =
    receiptFilter === "pending" ? pendingReceipts :
    receiptFilter === "approved" ? approvedReceipts :
    receiptFilter === "rejected" ? rejectedReceipts :
    receipts;
  const pendingCashouts = cashouts.filter((c) => c.status === "pending");

  return (
    <div className="page fade-in">
      <h1 style={{ fontSize: 32, fontWeight: 800, letterSpacing: -1, margin: 0 }}>
        {isReviewer ? "Reviewer Panel" : "Admin Panel"}
      </h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 14, marginTop: 6, marginBottom: 18 }}>
        {isReviewer
          ? "Review receipts from your assigned user."
          : "Review submissions and manage users."}
      </p>

      {loadState === "loading" && (
        <div data-testid="admin-loading" style={{
          display: "flex", flexDirection: "column", alignItems: "center", gap: 10,
          padding: "40px 20px", color: "var(--text-secondary)",
        }}>
          <div style={{
            width: 36, height: 36,
            border: "3px solid var(--border)",
            borderTopColor: "var(--primary)",
            borderRadius: "50%",
            animation: "cb-admin-spin 0.8s linear infinite",
          }} />
          <style>{`@keyframes cb-admin-spin { to { transform: rotate(360deg); } }`}</style>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Loading admin data…</div>
        </div>
      )}

      {loadState === "error" && (
        <AdminLoadError
          status={loadError?.status}
          message={loadError?.message}
          onRetry={load}
        />
      )}

      {loadState !== "loading" && (
      <>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 18 }}>
        <TabBtn active={tab === "receipts"} onClick={() => setTab("receipts")} testid="admin-tab-receipts">
          {`Receipts${pendingReceipts.length ? ` (${pendingReceipts.length})` : ""}`}
        </TabBtn>
        {!isReviewer && (
          <>
            <TabBtn active={tab === "cashouts"} onClick={() => setTab("cashouts")} testid="admin-tab-cashouts">
              {`Cashouts${pendingCashouts.length ? ` (${pendingCashouts.length})` : ""}`}
            </TabBtn>
            <TabBtn active={tab === "users"} onClick={() => setTab("users")} testid="admin-tab-users">
              Users
            </TabBtn>
            <TabBtn active={tab === "broadcast"} onClick={() => setTab("broadcast")} testid="admin-tab-broadcast">
              Broadcast
            </TabBtn>
            <TabBtn active={tab === "promo"} onClick={() => setTab("promo")} testid="admin-tab-promo">
              {`Promo${promoActive ? " ●" : ""}`}
            </TabBtn>
          </>
        )}
      </div>

      {tab === "receipts" && (
        <div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 14 }}>
            <FilterPill active={receiptFilter === "all"} onClick={() => setReceiptFilter("all")} testid="admin-rfilter-all">
              {`All (${receipts.length})`}
            </FilterPill>
            <FilterPill active={receiptFilter === "pending"} onClick={() => setReceiptFilter("pending")} testid="admin-rfilter-pending">
              {`Pending (${pendingReceipts.length})`}
            </FilterPill>
            <FilterPill active={receiptFilter === "approved"} onClick={() => setReceiptFilter("approved")} testid="admin-rfilter-approved">
              {`Approved (${approvedReceipts.length})`}
            </FilterPill>
            <FilterPill active={receiptFilter === "rejected"} onClick={() => setReceiptFilter("rejected")} testid="admin-rfilter-rejected">
              {`Rejected (${rejectedReceipts.length})`}
            </FilterPill>
          </div>
          {visibleReceipts.length === 0 && <Empty text={`No ${receiptFilter === "all" ? "" : receiptFilter + " "}receipts yet`} />}
          {visibleReceipts.map((r) => (
            <div key={r.id} data-testid={`admin-receipt-${r.id}`} style={cardStyle}>
              <div style={{ flex: 1 }}>
                <div style={cardTitle}>{r.user_name || r.user_email}</div>
                <div style={cardSub}>{r.user_email}</div>
                <div style={cardDate}>{new Date(r.created_at).toLocaleString()}</div>
                {r.amount != null && (
                  <div style={{ ...cardDate, color: "var(--success)" }}>
                    Credited £{Number(r.amount).toFixed(2)}
                  </div>
                )}
                {(r.cash_awarded || r.amount) ? (
                  <div style={{ ...cardDate, color: "#1FAE5E", fontWeight: 700 }}>
                    +£{Number(r.cash_awarded || r.amount || 0).toFixed(2)}
                  </div>
                ) : null}
                {r.ai && (
                  <div style={{
                    display: "inline-flex", alignItems: "center", gap: 6,
                    marginTop: 6, padding: "3px 8px", borderRadius: 999,
                    background: r.decided_by === "ai" ? "rgba(247,122,42,0.12)" : "var(--surface-elev)",
                    border: `1px solid ${r.decided_by === "ai" ? "rgba(247,122,42,0.35)" : "var(--border)"}`,
                    fontSize: 11, fontWeight: 800, color: "var(--text-primary)", letterSpacing: 0.3,
                  }}>
                    <span>{r.decided_by === "ai" ? "AI" : "AI scan"}</span>
                    {r.ai.brand && <span style={{ color: "var(--text-secondary)" }}>· {r.ai.brand}</span>}
                    {r.ai.total_amount != null && <span style={{ color: "var(--text-secondary)" }}>· £{Number(r.ai.total_amount).toFixed(2)}</span>}
                    {typeof r.ai.confidence_score === "number" && (
                      <span style={{ color: "var(--text-secondary)" }}>· {r.ai.confidence_score}%</span>
                    )}
                  </div>
                )}
                {r.status === "rejected" && r.admin_message && (
                  <div style={{ ...cardDate, color: "var(--danger)", marginTop: 4 }}>
                    {r.admin_message}
                  </div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <StatusPill status={r.status} />
                {r.status === "pending" ? (
                  <button
                    data-testid={`admin-receipt-review-${r.id}`}
                    onClick={() => openReceiptPreview(r.id, false)}
                    style={reviewBtn}
                  >
                    Review
                  </button>
                ) : (
                  <>
                    <button
                      data-testid={`admin-receipt-view-${r.id}`}
                      onClick={() => openReceiptPreview(r.id, true)}
                      style={viewBtn}
                    >
                      View
                    </button>
                    {r.status === "approved" && Number(r.cash_awarded || r.amount || 0) > 0 && (
                      <button
                        data-testid={`admin-receipt-proof-${r.id}`}
                        onClick={() => setProofReceipt(r)}
                        style={{
                          ...reviewBtn,
                          background: "var(--navy)", color: "#fff",
                          border: "1px solid var(--navy)",
                        }}
                      >
                        Proof
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "cashouts" && (
        <div>
          {cashouts.length === 0 && <Empty text="No cashout requests" />}
          {cashouts.map((c) => (
            <div key={c.id} data-testid={`admin-cashout-${c.id}`} style={cardStyle}>
              <div style={{ flex: 1 }}>
                <div style={cardTitle}>{c.user_name || c.user_email}</div>
                <div style={cardSub}>{c.user_email}</div>
                <div style={{ color: "var(--primary)", fontSize: 22, fontWeight: 800, marginTop: 8 }}>
                  £{c.amount.toFixed(2)}
                </div>
                <div style={{
                  background: "var(--surface-elev)", border: "1px solid var(--border)",
                  borderRadius: 10, padding: 10, marginTop: 8,
                }}>
                  {c.sort_code || c.account_number ? (
                    <>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>
                        Holder: <b style={{ color: "var(--text-primary)" }}>{c.account_name || "—"}</b>
                      </div>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 4 }}>
                        Sort: <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--text-primary)" }}>
                          {formatSortCodeDisplay(c.sort_code)}
                        </span>
                      </div>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 4 }}>
                        Account: <span style={{ fontFamily: "ui-monospace, monospace", color: "var(--text-primary)" }}>
                          {c.account_number}
                        </span>
                      </div>
                    </>
                  ) : c.bank_details ? (
                    <>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>Holder: {c.bank_details?.account_holder}</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>Sort: {c.bank_details?.sort_code}</div>
                      <div style={{ color: "var(--text-secondary)", fontSize: 12 }}>Acc: {c.bank_details?.account_number}</div>
                    </>
                  ) : (
                    <div style={{ color: "var(--text-muted)", fontSize: 12 }}>No payment method on file.</div>
                  )}
                </div>
                <div style={cardDate}>{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <StatusPill status={c.status} />
                {c.status === "pending" && (
                  <>
                    <button
                      data-testid={`admin-cashout-pay-${c.id}`}
                      onClick={() => payCashout(c.id)}
                      style={{ ...reviewBtn, background: "var(--success)", color: "#fff" }}
                    >
                      Mark Paid
                    </button>
                    <button
                      data-testid={`admin-cashout-reject-${c.id}`}
                      onClick={() => rejectCashout(c.id)}
                      style={{
                        ...reviewBtn, background: "rgba(239,68,68,0.15)",
                        border: "1px solid var(--error)", color: "var(--error)",
                      }}
                    >
                      Reject
                    </button>
                  </>
                )}
                {c.status === "paid" && (
                  <button
                    data-testid={`admin-cashout-proof-${c.id}`}
                    onClick={() => setProofCashout(c)}
                    style={{
                      ...reviewBtn,
                      background: "var(--navy)", color: "#fff",
                      border: "1px solid var(--navy)",
                    }}
                  >
                    Generate proof
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "users" && (
        <div>
          {users.map((u) => (
            <div key={u.id} data-testid={`admin-user-${u.id}`} style={cardStyle}>
              <div style={{ flex: 1 }}>
                <div style={cardTitle}>{u.name || u.email}</div>
                <div style={cardSub}>{u.email}</div>
                {u.role === "admin" && (
                  <div style={{ ...cardDate, color: "var(--primary)" }}>Admin</div>
                )}
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
                <div style={{ color: "var(--primary)", fontSize: 18, fontWeight: 800 }}>
                  £{Number(u.balance || 0).toFixed(2)}
                </div>
                {u.role !== "admin" && (
                  <button
                    data-testid={`admin-adjust-user-${u.id}`}
                    onClick={() => { setAdjustUser(u); setAdjustAmt(""); setAdjustMsg(""); }}
                    style={reviewBtn}
                  >
                    Adjust
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "broadcast" && (
        <div>
          <div data-testid="admin-broadcast-info" style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            background: "var(--peach-tint-soft)", border: "1px solid var(--peach-border)",
            borderRadius: 28, padding: 16, marginBottom: 8,
          }}>
            <Megaphone size={20} color="var(--primary)" style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: "var(--primary)", fontSize: 14, fontWeight: 800 }}>
                Broadcast to all users
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 4, lineHeight: 1.4 }}>
                This message will appear in every signed-up user's inbox ({recipients} recipient{recipients === 1 ? "" : "s"}). Admins are excluded.
              </div>
            </div>
          </div>

          <label className="label" style={{ marginTop: 14 }}>Title</label>
          <input
            data-testid="admin-broadcast-title"
            className="input"
            value={broadcastTitle}
            onChange={(e) => setBroadcastTitle(e.target.value)}
            placeholder="e.g. New eligible store added!"
            maxLength={120}
          />

          <label className="label" style={{ marginTop: 14 }}>Message</label>
          <textarea
            data-testid="admin-broadcast-body"
            className="input"
            value={broadcastBody}
            onChange={(e) => setBroadcastBody(e.target.value)}
            placeholder="Write the message you want every user to receive..."
            maxLength={2000}
            style={{ minHeight: 120, resize: "vertical", fontFamily: "inherit" }}
          />

          <button
            data-testid="admin-broadcast-send"
            className="btn-primary"
            onClick={sendBroadcast}
            disabled={broadcasting}
            style={{ width: "100%", marginTop: 18 }}
          >
            {broadcasting ? "Sending…" : "Send to All Users"}
          </button>
        </div>
      )}

      {tab === "promo" && (
        <div>
          <div data-testid="admin-promo-info" style={{
            display: "flex", alignItems: "flex-start", gap: 12,
            background: "var(--peach-tint-soft)", border: "1px solid var(--peach-border)",
            borderRadius: 20, padding: 16, marginBottom: 8,
          }}>
            <Megaphone size={20} color="var(--primary-dark)" style={{ marginTop: 2 }} />
            <div style={{ flex: 1 }}>
              <div style={{ color: "var(--primary-dark)", fontSize: 14, fontWeight: 800 }}>
                Home page promo banner
              </div>
              <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 4, lineHeight: 1.45 }}>
                This message shows under the wallet balance on every user's Home page. Use it for time-limited offers like "1.5x pay on KFC receipts this weekend".
              </div>
            </div>
          </div>

          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            marginTop: 12, marginBottom: 4,
          }}>
            <span
              data-testid="admin-promo-status"
              className="pill"
              style={promoActive
                ? { background: "rgba(76,175,120,0.15)", borderColor: "var(--success)", color: "var(--success)" }
                : { background: "var(--surface-soft)", borderColor: "var(--border)", color: "var(--text-muted)" }
              }
            >
              {promoActive ? "Live" : "Inactive"}
            </span>
          </div>

          <label className="label" style={{ marginTop: 10 }}>Promo message</label>
          <textarea
            data-testid="admin-promo-text"
            className="input"
            value={promoText}
            onChange={(e) => setPromoText(e.target.value)}
            placeholder='e.g. "1.5x pay on KFC receipts this weekend only!"'
            maxLength={200}
            style={{ minHeight: 90, resize: "vertical", fontFamily: "inherit" }}
          />
          <div style={{
            display: "flex", justifyContent: "space-between", marginTop: 6,
            color: "var(--text-muted)", fontSize: 11, fontWeight: 600,
          }}>
            <span>Keep it short — shown on one line on mobile.</span>
            <span>{promoText.length}/200</span>
          </div>

          {/* Live preview */}
          <label className="label" style={{ marginTop: 16 }}>Preview</label>
          <div style={{
            background: "var(--navy)", borderRadius: 16, padding: 14,
          }}>
            {promoText.trim() ? (
              <div data-testid="admin-promo-preview" style={{
                display: "flex", alignItems: "center", gap: 10,
                background: "linear-gradient(135deg, #F77A2A 0%, #E25C12 100%)",
                borderRadius: 14, padding: "10px 14px",
                boxShadow: "0 10px 26px -14px rgba(226, 92, 18, 0.7)",
              }}>
                <span style={{
                  width: 30, height: 30, borderRadius: 999, flexShrink: 0,
                  background: "rgba(255,255,255,0.18)",
                  border: "1px solid rgba(255,255,255,0.35)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <Sparkles size={16} color="#FFFFFF" />
                </span>
                <span style={{ color: "#FFFFFF", fontSize: 13, fontWeight: 600, lineHeight: 1.35, flex: 1 }}>
                  {promoText}
                </span>
              </div>
            ) : (
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, textAlign: "center", padding: "10px 0" }}>
                Enter some text above to see the preview
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button
              data-testid="admin-promo-save"
              className="btn-primary"
              onClick={savePromo}
              disabled={promoSaving || !promoText.trim()}
              style={{ flex: 1 }}
            >
              {promoSaving
                ? "Saving…"
                : promoActive
                  ? "Update banner"
                  : "Publish banner"}
            </button>
            <button
              data-testid="admin-promo-clear"
              onClick={clearPromo}
              disabled={promoSaving || (!promoActive && !promoText)}
              style={{
                flex: 1,
                background: "var(--surface)",
                border: "1px solid var(--error)",
                color: "var(--error)",
                borderRadius: 999, padding: "14px 16px",
                fontWeight: 800, fontSize: 14,
                opacity: promoSaving || (!promoActive && !promoText) ? 0.5 : 1,
              }}
            >
              Remove banner
            </button>
          </div>
        </div>
      )}
      </>
      )}

      {/* Receipt preview modal */}
      {previewId && (
        <Modal
          onClose={closePreview}
          title={previewReadOnly ? "Receipt details" : "Review Receipt"}
          footer={previewReadOnly ? (
            <button
              data-testid="admin-preview-close"
              onClick={closePreview}
              style={{ ...modalBtn("var(--surface-elev)"), color: "var(--text-primary)", border: "1px solid var(--border)" }}
            >
              Close
            </button>
          ) : (
            <div style={{ display: "flex", gap: 10 }}>
              <button
                data-testid="admin-approve-button"
                onClick={approveReceipt}
                disabled={busy}
                style={modalBtn("var(--success)")}
              >
                {busy ? "…" : "Approve & Credit"}
              </button>
              <button
                data-testid="admin-reject-button"
                onClick={rejectReceipt}
                disabled={busy}
                style={modalBtn("var(--error)")}
              >
                Reject
              </button>
            </div>
          )}
        >
          {previewImg ? (
            <>
              <img src={previewImg} alt="receipt" style={{
                width: "100%", maxHeight: "38vh", objectFit: "contain",
                borderRadius: 16, background: "var(--surface-elev)", display: "block",
              }} />
              <button
                data-testid="admin-download-receipt"
                onClick={() => downloadReceipt(previewImg, previewId)}
                style={{
                  marginTop: 10,
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: "var(--surface-elev)", border: "1px solid var(--border)",
                  color: "var(--text-primary)", borderRadius: 999,
                  padding: "8px 14px", fontWeight: 700, fontSize: 13,
                }}
              >
                <Download size={16} color="var(--primary)" />
                Download image
              </button>
            </>
          ) : (
            <div style={{ textAlign: "center", padding: 24 }}>Loading…</div>
          )}

          {previewReadOnly ? (
            <ReceiptReadOnlyDetails receipt={previewMeta} />
          ) : (
            <>
              <label className="label" style={{ marginTop: 14 }}>
                Cash to credit (£) <span style={{ color: "var(--error)" }}>*</span>
              </label>
              <input
                data-testid="admin-approve-amount"
                className="input"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={actionInput}
                onChange={(e) => setActionInput(e.target.value)}
                placeholder="e.g. 2.30  (goes straight to their wallet)"
              />
            </>
          )}
        </Modal>
      )}

      {/* Adjust balance modal */}
      {adjustUser && (
        <Modal
          onClose={() => setAdjustUser(null)}
          title="Adjust Balance"
          footer={(
            <button
              data-testid="admin-adjust-submit"
              className="btn-primary"
              onClick={submitAdjust}
              style={{ width: "100%" }}
            >
              Apply
            </button>
          )}
        >
          <div style={cardTitle}>{adjustUser?.name}</div>
          <div style={cardSub}>{adjustUser?.email} • Current £{Number(adjustUser?.balance || 0).toFixed(2)}</div>

          <label className="label" style={{ marginTop: 14 }}>Amount (£) — use negative to deduct</label>
          <input
            data-testid="admin-adjust-amount-input"
            className="input"
            value={adjustAmt}
            onChange={(e) => setAdjustAmt(e.target.value)}
            placeholder="e.g. 5 or -2.50"
          />

          <label className="label" style={{ marginTop: 14 }}>Message (optional)</label>
          <textarea
            data-testid="admin-adjust-message-input"
            className="input"
            value={adjustMsg}
            onChange={(e) => setAdjustMsg(e.target.value)}
            placeholder="e.g. Bonus for referring a friend"
            style={{ minHeight: 80, resize: "vertical", fontFamily: "inherit" }}
          />
        </Modal>
      )}

      {proofCashout && (
        <PayoutProofModal
          cashout={proofCashout}
          onClose={() => setProofCashout(null)}
        />
      )}

      {proofReceipt && (
        <ReceiptProofModal
          receipt={proofReceipt}
          onClose={() => setProofReceipt(null)}
        />
      )}
    </div>
  );
}

/* ---------- Payout proof card — screenshot-ready summary ---------- */
function PayoutProofModal({ cashout, onClose }) {
  const paidAt = cashout.decided_at ? new Date(cashout.decided_at) : new Date();
  return (
    <div
      data-testid="payout-proof-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(14, 34, 64, 0.86)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="payout-proof-card"
        style={{
          width: "100%", maxWidth: 380,
          background: "linear-gradient(160deg, #FFE7CF 0%, #FFF2E1 55%, #FFDBB8 100%)",
          borderRadius: 28, padding: "26px 20px 22px",
          position: "relative",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.45)",
          overflow: "hidden",
          border: "1px solid var(--peach-border)",
        }}
      >
        {/* Decorative confetti dots */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {[
            { c: "#F77A2A", t: 18, l: "12%", s: 5 },
            { c: "#E63B5A", t: 42, l: "88%", s: 6 },
            { c: "#2EBFA6", t: 90, l: "8%", s: 4 },
            { c: "#FFC72C", t: 220, l: "78%", s: 5 },
            { c: "#F77A2A", t: 280, l: "18%", s: 5 },
          ].map((d, i) => (
            <span key={i} style={{
              position: "absolute", top: d.t, left: d.l,
              width: d.s, height: d.s, borderRadius: 999, background: d.c, opacity: 0.85,
            }} />
          ))}
        </div>

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="payout-proof-close"
          style={{
            position: "absolute", top: 10, right: 10,
            width: 30, height: 30, borderRadius: 15,
            background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.06)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 2,
          }}
        >
          <X size={15} color="var(--text-secondary)" />
        </button>

        {/* Brand mark */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          marginBottom: 12, position: "relative",
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 10, background: "var(--primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 18px -8px rgba(247,122,42,0.55)",
          }}>
            <span style={{ color: "#FFFFFF", fontWeight: 900, fontSize: 15 }}>£</span>
          </span>
          <span style={{
            color: "var(--navy)", fontWeight: 900, fontSize: 15, letterSpacing: -0.2,
          }}>
            Mrs CashBack
          </span>
        </div>

        {/* Big label */}
        <div style={{
          textAlign: "center", color: "var(--navy)",
          fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase",
          opacity: 0.7,
        }}>
          Cashback delivered
        </div>
        <div style={{
          textAlign: "center",
          fontSize: 68, fontWeight: 900, letterSpacing: -3.5, color: "var(--primary)",
          lineHeight: 1, marginTop: 6, position: "relative",
        }}>
          £{Number(cashout.amount).toFixed(2)}
        </div>
        <div style={{
          textAlign: "center", color: "var(--text-secondary)", fontSize: 12,
          marginTop: 8, fontWeight: 700,
        }}>
          Sent to <b style={{ color: "var(--navy)" }}>{cashout.account_name || cashout.user_name || cashout.user_email}</b>
        </div>

        {/* Receipt-style detail block */}
        <div style={{
          background: "rgba(255,255,255,0.7)",
          border: "1px dashed var(--peach-border-strong)",
          borderRadius: 16, padding: "12px 14px",
          marginTop: 16, position: "relative",
        }}>
          <ProofRow label="Paid" value={paidAt.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
          <ProofRow label="Method" value="UK bank transfer" />
          <ProofRow label="Ref" value={cashout.id.slice(0, 8).toUpperCase()} />
        </div>

        <div style={{
          textAlign: "center", marginTop: 14,
          color: "var(--text-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        }}>
          mrscashback.co.uk · real cash, real receipts
        </div>

        <div style={{ marginTop: 14, textAlign: "center" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(11,36,71,0.08)",
            border: "1px solid rgba(11,36,71,0.15)",
            borderRadius: 999, padding: "6px 12px",
            color: "var(--text-secondary)", fontSize: 11, fontWeight: 700,
          }}>
            Screenshot & share as proof
          </span>
        </div>
      </div>
    </div>
  );
}

function ProofRow({ label, value }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "baseline",
      fontSize: 12, marginTop: 4,
    }}>
      <span style={{ color: "var(--text-secondary)", fontWeight: 700 }}>{label}</span>
      <span style={{ color: "var(--navy)", fontWeight: 800, letterSpacing: 0.2 }}>{value}</span>
    </div>
  );
}

/* ---------- Receipt proof card — for approved receipts ---------- */
function ReceiptProofModal({ receipt, onClose }) {
  const paidAt = receipt.decided_at ? new Date(receipt.decided_at) : new Date();
  const cash = Number(receipt.cash_awarded || receipt.amount || 0);
  const userLabel = receipt.user_name || receipt.user_email || "user";
  return (
    <div
      data-testid="receipt-proof-modal"
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(14, 34, 64, 0.86)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 18, backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="receipt-proof-card"
        style={{
          width: "100%", maxWidth: 380,
          background: "linear-gradient(160deg, #E9F9EE 0%, #FFF8ED 55%, #FFE1C9 100%)",
          borderRadius: 28, padding: "26px 20px 22px",
          position: "relative",
          boxShadow: "0 30px 60px -24px rgba(0,0,0,0.45)",
          overflow: "hidden",
          border: "1px solid var(--peach-border)",
        }}
      >
        {/* Decorative confetti dots */}
        <div aria-hidden style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          {[
            { c: "#1FAE5E", t: 18, l: "12%", s: 5 },
            { c: "#F77A2A", t: 42, l: "88%", s: 6 },
            { c: "#E63B5A", t: 90, l: "8%", s: 4 },
            { c: "#FFC72C", t: 220, l: "78%", s: 5 },
            { c: "#1FAE5E", t: 280, l: "18%", s: 5 },
          ].map((d, i) => (
            <span key={i} style={{
              position: "absolute", top: d.t, left: d.l,
              width: d.s, height: d.s, borderRadius: 999, background: d.c, opacity: 0.85,
            }} />
          ))}
        </div>

        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="receipt-proof-close"
          style={{
            position: "absolute", top: 10, right: 10,
            width: 30, height: 30, borderRadius: 15,
            background: "rgba(255,255,255,0.7)", border: "1px solid rgba(0,0,0,0.06)",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer", zIndex: 2,
          }}
        >
          <X size={15} color="var(--text-secondary)" />
        </button>

        {/* Brand mark */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
          marginBottom: 12, position: "relative",
        }}>
          <span style={{
            width: 34, height: 34, borderRadius: 10, background: "var(--primary)",
            display: "inline-flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 8px 18px -8px rgba(247,122,42,0.55)",
          }}>
            <span style={{ color: "#FFFFFF", fontWeight: 900, fontSize: 15 }}>£</span>
          </span>
          <span style={{
            color: "var(--navy)", fontWeight: 900, fontSize: 15, letterSpacing: -0.2,
          }}>
            Mrs CashBack
          </span>
        </div>

        {/* Big label */}
        <div style={{
          textAlign: "center", color: "var(--navy)",
          fontSize: 11, fontWeight: 900, letterSpacing: 2, textTransform: "uppercase",
          opacity: 0.7,
        }}>
          User paid
        </div>
        <div style={{
          textAlign: "center",
          fontSize: 68, fontWeight: 900, letterSpacing: -3.5, color: "#1FAE5E",
          lineHeight: 1, marginTop: 6, marginBottom: 12, position: "relative",
        }}>
          £{cash.toFixed(2)}
        </div>

        {/* Detail block */}
        <div style={{
          background: "rgba(255,255,255,0.7)",
          border: "1px dashed rgba(31,174,94,0.45)",
          borderRadius: 16, padding: "12px 14px",
          marginTop: 16, position: "relative",
        }}>
          <ProofRow label="User" value={userLabel} />
          {receipt.ai?.total_amount != null && (
            <ProofRow label="Receipt total" value={`£${Number(receipt.ai.total_amount).toFixed(2)}`} />
          )}
          <ProofRow label="Approved" value={paidAt.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })} />
          <ProofRow label="Ref" value={receipt.id.slice(0, 8).toUpperCase()} />
        </div>

        <div style={{
          textAlign: "center", marginTop: 14,
          color: "var(--text-secondary)", fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
        }}>
          mrscashback.co.uk · real cash, real receipts
        </div>

        <div style={{ marginTop: 14, textAlign: "center" }}>
          <span style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            background: "rgba(11,36,71,0.08)",
            border: "1px solid rgba(11,36,71,0.15)",
            borderRadius: 999, padding: "6px 12px",
            color: "var(--text-secondary)", fontSize: 11, fontWeight: 700,
          }}>
            Screenshot & share as proof
          </span>
        </div>
      </div>
    </div>
  );
}

function formatSortCodeDisplay(sc) {
  const digits = (sc || "").replace(/\D/g, "");
  if (digits.length !== 6) return sc || "—";
  return `${digits.slice(0, 2)}-${digits.slice(2, 4)}-${digits.slice(4, 6)}`;
}


function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: "10px 14px", borderRadius: 999,
        background: active ? "var(--primary)" : "var(--surface-elev)",
        border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
        color: active ? "var(--bg)" : "var(--text-primary)",
        fontWeight: 800, fontSize: 13,
        transition: "background .15s ease",
      }}
    >
      {children}
    </button>
  );
}

function Empty({ text }) {
  return (
    <div style={{ textAlign: "center", padding: 30 }}>
      <FolderOpen size={40} color="var(--text-muted)" style={{ margin: "0 auto" }} />
      <div style={{ color: "var(--text-secondary)", marginTop: 8 }}>{text}</div>
    </div>
  );
}

function Modal({ title, onClose, children, footer }) {
  // Hide the global bottom tab bar while a modal is open so the content
  // (and sticky footer) is never covered by the navigation on mobile.
  useEffect(() => {
    const tab = document.querySelector(".tab-bar");
    if (tab) tab.style.display = "none";
    document.body.style.overflow = "hidden";
    return () => {
      if (tab) tab.style.display = "";
      document.body.style.overflow = "";
    };
  }, []);

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0,
          background: "rgba(14, 34, 64, 0.55)",
          zIndex: 99,
        }}
      />
      {/* Modal — portaled to document.body to escape any ancestor transforms */}
      <div
        role="dialog"
        aria-modal="true"
        style={{
          position: "fixed",
          left: "50%",
          top: 12,
          bottom: 12,
          transform: "translateX(-50%)",
          width: "calc(100% - 24px)",
          maxWidth: 560,
          background: "var(--surface)",
          borderRadius: 22,
          border: "1px solid var(--border)",
          display: "flex", flexDirection: "column",
          boxShadow: "0 12px 40px rgba(14, 34, 64, 0.28)",
          overflow: "hidden",
          zIndex: 100,
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 18px 12px", borderBottom: "1px solid var(--border)",
          flexShrink: 0, background: "var(--surface)",
        }}>
          <div style={{ fontSize: 19, fontWeight: 800 }}>{title}</div>
          <button onClick={onClose} aria-label="Close">
            <X size={24} color="var(--text-primary)" />
          </button>
        </div>
        <div style={{ padding: 18, overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
          {children}
        </div>
        {footer && (
          <div style={{
            flexShrink: 0,
            padding: "12px 18px 14px",
            borderTop: "1px solid var(--border)",
            background: "var(--surface)",
          }}>
            {footer}
          </div>
        )}
      </div>
    </>,
    document.body
  );
}

const cardStyle = {
  display: "flex", gap: 12,
  background: "var(--surface)", border: "1px solid var(--border)",
  borderRadius: 28, padding: 16, marginBottom: 12,
};
const cardTitle = { color: "var(--text-primary)", fontSize: 15, fontWeight: 800 };
const cardSub = { color: "var(--text-secondary)", fontSize: 12, marginTop: 2 };
const cardDate = { color: "var(--text-muted)", fontSize: 11, marginTop: 6 };
const reviewBtn = {
  background: "var(--primary)", color: "#FFFFFF",
  borderRadius: 999, padding: "8px 14px",
  fontWeight: 800, fontSize: 12, letterSpacing: 0.4,
};
const viewBtn = {
  background: "var(--surface-elev)",
  color: "var(--text-primary)",
  border: "1px solid var(--border)",
  borderRadius: 999, padding: "8px 14px",
  fontWeight: 800, fontSize: 12, letterSpacing: 0.4,
};
function AdminLoadError({ status, message, onRetry }) {
  const isAuthErr = status === 401 || status === 403;
  return (
    <div data-testid="admin-load-error" style={{
      background: "var(--surface)",
      border: "1px solid var(--border)",
      borderRadius: 18,
      padding: "20px 18px",
      textAlign: "center",
      marginBottom: 18,
    }}>
      <div style={{
        width: 52, height: 52, borderRadius: 26, margin: "0 auto 10px",
        background: "rgba(230,59,90,0.12)",
        display: "flex", alignItems: "center", justifyContent: "center",
      }}>
        <AlertTriangle size={24} color="#E63B5A" />
      </div>
      <div style={{ color: "var(--navy)", fontSize: 16, fontWeight: 800, marginBottom: 4 }}>
        {isAuthErr ? "Your session expired" : "Couldn't load admin data"}
      </div>
      <div style={{
        color: "var(--text-secondary)", fontSize: 13, lineHeight: 1.45,
        marginBottom: 14, padding: "0 8px",
      }}>
        {isAuthErr
          ? "Please sign in again to continue."
          : (message || "Something went wrong. Please check your connection and try again.")}
      </div>
      <button
        data-testid="admin-load-retry"
        onClick={isAuthErr ? () => { window.location.href = "/login"; } : onRetry}
        className="btn-primary"
        style={{ padding: "12px 22px", fontSize: 13, fontWeight: 900, letterSpacing: 0.8, textTransform: "uppercase" }}
      >
        {isAuthErr ? "Sign in" : "Try again"}
      </button>
    </div>
  );
}

function FilterPill({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: 999,
        fontSize: 12, fontWeight: 800, letterSpacing: 0.3,
        background: active ? "var(--primary)" : "var(--surface-elev)",
        color: active ? "#FFFFFF" : "var(--text-primary)",
        border: `1px solid ${active ? "var(--primary)" : "var(--border)"}`,
      }}
    >
      {children}
    </button>
  );
}
function ReceiptReadOnlyDetails({ receipt }) {
  if (!receipt) return null;
  const ai = receipt.ai || {};
  const decidedBy = receipt.decided_by === "ai" ? "Auto-decided by AI" :
                    receipt.decided_by === "admin" ? "Decided by admin" : null;
  const rows = [
    ["Status", receipt.status],
    ["User", `${receipt.user_name || ""} ${receipt.user_email ? `(${receipt.user_email})` : ""}`.trim()],
    ["Submitted", new Date(receipt.created_at).toLocaleString()],
    receipt.decided_at && ["Decided", new Date(receipt.decided_at).toLocaleString()],
    decidedBy && ["Authority", decidedBy],
    (receipt.cash_awarded || receipt.amount) != null && ["Cash credited", `£${Number(receipt.cash_awarded || receipt.amount || 0).toFixed(2)}`],
    ai.brand && ["Brand", String(ai.brand)],
    ai.total_amount != null && ["AI total", `£${Number(ai.total_amount).toFixed(2)}`],
    ai.receipt_date && ["Receipt date", String(ai.receipt_date) + (ai.receipt_time ? ` ${ai.receipt_time}` : "")],
    ai.store_number && ["Store", String(ai.store_number)],
    ai.transaction_number && ["Trans #", String(ai.transaction_number)],
    typeof ai.confidence_score === "number" && ["AI confidence", `${ai.confidence_score}%`],
    typeof ai.legibility_score === "number" && ["Legibility", `${ai.legibility_score}%`],
    Array.isArray(ai.flags) && ai.flags.length > 0 && ["AI flags", ai.flags.join(", ")],
    receipt.admin_message && ["Decision note", receipt.admin_message],
    ai.reason && ai.reason !== receipt.admin_message && ["AI reason", ai.reason],
  ].filter(Boolean);
  return (
    <div style={{ marginTop: 14, display: "grid", gap: 6 }}>
      {rows.map(([label, value]) => (
        <div key={label} style={{
          display: "flex", gap: 10, alignItems: "flex-start",
          fontSize: 13, lineHeight: 1.4,
          padding: "6px 0", borderBottom: "1px solid var(--border)",
        }}>
          <span style={{
            color: "var(--text-secondary)", fontWeight: 700,
            minWidth: 110, flexShrink: 0,
          }}>{label}</span>
          <span style={{ color: "var(--text-primary)", wordBreak: "break-word" }}>{String(value)}</span>
        </div>
      ))}
    </div>
  );
}
function modalBtn(bg) {
  return {
    flex: 1, paddingTop: 14, paddingBottom: 14,
    background: bg, color: "#fff", borderRadius: 999,
    fontWeight: 800, fontSize: 15,
  };
}
